import * as React from "react";
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from "date-fns/locale/fr";
import startOfDay from "date-fns/startOfDay";
import addMinutes from "date-fns/addMinutes";
import differenceInMinutes from "date-fns/differenceInMinutes";
import format from "date-fns/format";
import Dropdown from "react-bootstrap/Dropdown";
import DropToggleButtonFRef from "./DropToggleButtonFRef";

interface IDepartureTimePickerProps {
  onDateChanged: (date: Date) => void;
  date: Date;
}

export default class DepartureTimePicker extends React.PureComponent<
  IDepartureTimePickerProps
> {
  private datePickerToggleRef: any = undefined;

  constructor(props: IDepartureTimePickerProps) {
    super(props);

    this.getTimeItems = this.getTimeItems.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleTimeSelected = this.handleTimeSelected.bind(this);
  }

  private handleDateChange(date: Date | null) {
    date = date ?? new Date();
    this.datePickerToggleRef.click();
    const selectedDate = startOfDay(date);
    const selectedTime = Math.abs(
      differenceInMinutes(startOfDay(this.props.date), this.props.date)
    );
    const dateValue = addMinutes(selectedDate, selectedTime);
    this.props.onDateChanged(dateValue);
  }

  private handleTimeSelected(eventKey: string, _event?: Object) {
    const dateValue = addMinutes(startOfDay(this.props.date), Number(eventKey));
    this.props.onDateChanged(dateValue);
  }

  private getTimeItems() {
    const timeInterval: number = 15;
    const nbTimes: number = 1440 / timeInterval;
    const items: JSX.Element[] = [];
    const currentDate = new Date();
    const baseTime = startOfDay(currentDate);
    const selectedDate = startOfDay(this.props.date);
    const selectedTime = Math.abs(
      differenceInMinutes(selectedDate, this.props.date)
    );
    for (let i = 0; i < nbTimes; i++) {
      let currInterval = i * timeInterval;
      let optionDate = addMinutes(baseTime, currInterval);
      items.push(
        <Dropdown.Item
          onSelect={() => this.handleTimeSelected(currInterval.toString())}
          active={selectedTime === currInterval}
          key={currInterval}
        >
          {format(optionDate, "HH:mm")}
        </Dropdown.Item>
      );
    }
    return items;
  }

  render() {
    let selectedDate = startOfDay(this.props.date);
    let selectedTime = addMinutes(
      selectedDate,
      Math.abs(differenceInMinutes(selectedDate, this.props.date))
    );

    return (
      <div className="input-border d-flex">
        <div className="dtime-pick-container">
          <div className="dtime-pick-ico">
            <svg viewBox="0 0 36.447 36.447">
              <path
                d="M30.224,3.948h-1.098V2.75c0-1.517-1.197-2.75-2.67-2.75c-1.474,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75
			c0-1.517-1.197-2.75-2.67-2.75c-1.473,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75c0-1.517-1.197-2.75-2.67-2.75
			c-1.473,0-2.67,1.233-2.67,2.75v1.197H6.224c-2.343,0-4.25,1.907-4.25,4.25v24c0,2.343,1.907,4.25,4.25,4.25h24
			c2.344,0,4.25-1.907,4.25-4.25v-24C34.474,5.855,32.567,3.948,30.224,3.948z M25.286,2.75c0-0.689,0.525-1.25,1.17-1.25
			c0.646,0,1.17,0.561,1.17,1.25v4.896c0,0.689-0.524,1.25-1.17,1.25c-0.645,0-1.17-0.561-1.17-1.25V2.75z M17.206,2.75
			c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25v4.896c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25V2.75z M9.125,2.75
			c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25v4.896c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25V2.75z
			 M31.974,32.198c0,0.965-0.785,1.75-1.75,1.75h-24c-0.965,0-1.75-0.785-1.75-1.75v-22h27.5V32.198z"
              />

              <rect x="6.724" y="14.626" width="4.595" height="4.089" />
              <rect x="12.857" y="14.626" width="4.596" height="4.089" />
              <rect x="18.995" y="14.626" width="4.595" height="4.089" />
              <rect x="25.128" y="14.626" width="4.596" height="4.089" />
              <rect x="6.724" y="20.084" width="4.595" height="4.086" />
              <rect x="12.857" y="20.084" width="4.596" height="4.086" />
              <rect x="18.995" y="20.084" width="4.595" height="4.086" />
              <rect x="25.128" y="20.084" width="4.596" height="4.086" />
              <rect x="6.724" y="25.54" width="4.595" height="4.086" />
              <rect x="12.857" y="25.54" width="4.596" height="4.086" />
              <rect x="18.995" y="25.54" width="4.595" height="4.086" />
              <rect x="25.128" y="25.54" width="4.596" height="4.086" />
            </svg>
          </div>
          <Dropdown className="dtime-pick">
            <Dropdown.Toggle
              ref={(toggleRef: any) => {
                this.datePickerToggleRef = toggleRef;
              }}
              as={DropToggleButtonFRef}
              id="dropdown-custom-components"
            >
              {format(selectedDate, "dd/MM/yyyy")}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0 overflow-hidden">
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
                <DateCalendar
                  disablePast
                  views={["day"]}
                  value={selectedDate}
                  onChange={this.handleDateChange}
                />
              </LocalizationProvider>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="flex-fill in-input-h-separator">
          <div></div>
        </div>
        <div className="dtime-pick-container">
          <div className="dtime-pick-ico">
            <svg viewBox="0 0 512 512">
              <path
                d="M437.02,74.98C388.667,26.629,324.38,0,256,0S123.333,26.629,74.98,74.98C26.629,123.333,0,187.62,0,256
			s26.629,132.667,74.98,181.02C123.333,485.371,187.62,512,256,512c46.813,0,92.617-12.757,132.461-36.893
			c9.448-5.723,12.468-18.021,6.745-27.468c-5.722-9.449-18.022-12.468-27.468-6.745C334.143,461.244,295.505,472,256,472
			c-119.103,0-216-96.897-216-216S136.897,40,256,40s216,96.897,216,216c0,42.589-12.665,84.044-36.627,119.885
			c-6.139,9.182-3.672,21.603,5.511,27.742c9.183,6.139,21.603,3.671,27.742-5.511C497.001,355.674,512,306.531,512,256
			C512,187.62,485.371,123.333,437.02,74.98z"
              />
              <path
                d="M256,76c-11.046,0-20,8.954-20,20v151.716l-73.338,73.338c-7.811,7.81-7.811,20.473,0,28.284
			c3.905,3.905,9.023,5.858,14.142,5.858s10.237-1.953,14.142-5.858l79.196-79.196c3.751-3.75,5.858-8.838,5.858-14.142V96
			C276,84.954,267.046,76,256,76z"
              />
            </svg>
          </div>
          <Dropdown className="dtime-pick">
            <Dropdown.Toggle
              as={DropToggleButtonFRef}
              id="dropdown-custom-components"
            >
              {format(selectedTime, "HH:mm")}
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ overflowY: "scroll", maxHeight: "15rem" }}>
              {this.getTimeItems()}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  }
}
